import { supabase } from '@/integrations/supabase/client';
import type { ReschedulingStrategy, UserPlanningPreferences, PlatformSchedules, ToneOfVoice } from '@/types/scheduling';

/**
 * Get planning preferences for a user
 */
export async function getUserPlanningPreferences(userId: string): Promise<{ 
  success: boolean; 
  data: UserPlanningPreferences | null; 
  error?: any 
}> {
  try {
    const { data, error } = await supabase
      .from('user_planning_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching planning preferences:', error);
      return { success: false, data: null, error };
    }

    return { success: true, data: data as UserPlanningPreferences | null };
  } catch (error) {
    console.error('Error in getUserPlanningPreferences:', error);
    return { success: false, data: null, error };
  }
}

/**
 * Upsert rescheduling strategy for a user (step 2)
 * Pass null to clear the strategy
 */
export async function upsertPlanningPreferences(
  userId: string, 
  strategy: ReschedulingStrategy | null
): Promise<{ success: boolean; error?: any }> {
  try {
    const { error } = await supabase
      .from('user_planning_preferences')
      .upsert({
        user_id: userId,
        rescheduling_strategy: strategy,
        is_rescheduling_configured: strategy !== null,
        updated_at: new Date().toISOString()
      }, { 
        onConflict: 'user_id' 
      });

    if (error) {
      console.error('Error upserting planning preferences:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in upsertPlanningPreferences:', error);
    return { success: false, error };
  }
}

/**
 * Upsert planning setup for a user (step 1)
 */
export async function upsertPlanningSetup(
  userId: string,
  globalFrequency: number,
  platformSchedules: PlatformSchedules,
  isAutoPlanning?: boolean,
  timezone?: string
): Promise<{ success: boolean; error?: any }> {
  try {
    // Use type assertion to bypass strict typing for JSONB field
    const payload: any = {
      user_id: userId,
      global_frequency: globalFrequency,
      platform_schedules: platformSchedules,
      is_planning_configured: true,
      updated_at: new Date().toISOString()
    };
    
    // Only include is_auto_planning if explicitly provided
    if (isAutoPlanning !== undefined) {
      payload.is_auto_planning = isAutoPlanning;
    }
    
    // Only include timezone if explicitly provided
    if (timezone !== undefined) {
      payload.timezone = timezone;
    }
    
    const { error } = await supabase
      .from('user_planning_preferences')
      .upsert(payload, { 
        onConflict: 'user_id' 
      });

    if (error) {
      console.error('Error upserting planning setup:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in upsertPlanningSetup:', error);
    return { success: false, error };
  }
}

/**
 * Update user timezone
 */
export async function updateUserTimezone(
  userId: string,
  timezone: string
): Promise<{ success: boolean; error?: any }> {
  try {
    const { error } = await supabase
      .from('user_planning_preferences')
      .upsert({
        user_id: userId,
        timezone,
        updated_at: new Date().toISOString()
      }, { 
        onConflict: 'user_id' 
      });

    if (error) {
      console.error('Error updating timezone:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in updateUserTimezone:', error);
    return { success: false, error };
  }
}

/**
 * Upsert Instagram crosspost settings for a user
 */
export async function upsertInstagramCrosspostSettings(
  userId: string,
  enabled: boolean,
  mentions: string[]
): Promise<{ success: boolean; error?: any }> {
  try {
    // Ensure mentions array has max 2 elements
    const sanitizedMentions = mentions.slice(0, 2);
    
    const { error } = await supabase
      .from('user_planning_preferences')
      .upsert({
        user_id: userId,
        instagram_crosspost_enabled: enabled,
        instagram_crosspost_mentions: sanitizedMentions,
        updated_at: new Date().toISOString()
      }, { 
        onConflict: 'user_id' 
      });

    if (error) {
      console.error('Error upserting Instagram crosspost settings:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in upsertInstagramCrosspostSettings:', error);
    return { success: false, error };
  }
}

/**
 * Reset Instagram crosspost settings to null (not answered in onboarding)
 */
export async function resetInstagramCrosspostSettings(userId: string): Promise<{ success: boolean; error?: any }> {
  try {
    const { error } = await supabase
      .from('user_planning_preferences')
      .update({
        instagram_crosspost_enabled: null,
        instagram_crosspost_mentions: null,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (error) {
      console.error('Error resetting Instagram crosspost settings:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in resetInstagramCrosspostSettings:', error);
    return { success: false, error };
  }
}

/**
 * Reset planning preferences for a user (admin function)
 */
export async function resetPlanningPreferences(userId: string): Promise<{ success: boolean; error?: any }> {
  try {
    const { error } = await supabase
      .from('user_planning_preferences')
      .delete()
      .eq('user_id', userId);

    if (error) {
      console.error('Error resetting planning preferences:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in resetPlanningPreferences:', error);
    return { success: false, error };
  }
}

/**
 * Get all users with their planning preferences (admin only)
 */
export async function getAllUsersWithPreferences(): Promise<{
  success: boolean;
  data: Array<{
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    planning_preferences: UserPlanningPreferences | null;
  }> | null;
  error?: any;
}> {
  try {
    // First get all active users
    const { data: users, error: usersError } = await supabase
      .from('users')
      .select('id, email, first_name, last_name')
      .eq('is_active', true)
      .order('email');

    if (usersError) {
      console.error('Error fetching users:', usersError);
      return { success: false, data: null, error: usersError };
    }

    // Then get all planning preferences
    const { data: preferences, error: prefsError } = await supabase
      .from('user_planning_preferences')
      .select('*');

    if (prefsError) {
      console.error('Error fetching preferences:', prefsError);
      return { success: false, data: null, error: prefsError };
    }

    // Map preferences to users
    const result = users?.map(user => ({
      ...user,
      planning_preferences: preferences?.find(p => p.user_id === user.id) as UserPlanningPreferences | null || null
    })) || [];

    return { success: true, data: result };
  } catch (error) {
    console.error('Error in getAllUsersWithPreferences:', error);
    return { success: false, data: null, error };
  }
}

/**
 * Upsert description preferences for a user (tone of voice, recurring CTA, and emojis)
 */
export async function upsertDescriptionPreferences(
  userId: string,
  toneOfVoice: ToneOfVoice,
  hasRecurringCta: boolean,
  recurringCtaText: string | null,
  hasEmojis: boolean = true
): Promise<{ success: boolean; error?: any }> {
  try {
    const { error } = await supabase
      .from('user_planning_preferences')
      .upsert({
        user_id: userId,
        tone_of_voice: toneOfVoice,
        has_recurring_cta: hasRecurringCta,
        recurring_cta_text: recurringCtaText,
        has_emojis: hasEmojis,
        updated_at: new Date().toISOString()
      }, { 
        onConflict: 'user_id' 
      });

    if (error) {
      console.error('Error upserting description preferences:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in upsertDescriptionPreferences:', error);
    return { success: false, error };
  }
}

/**
 * Partial update for description preferences (admin use)
 * Only updates the fields that are provided
 */
export async function updateDescriptionPreferencesPartial(
  userId: string,
  changes: {
    tone_of_voice?: ToneOfVoice | null;
    has_emojis?: boolean | null;
    has_recurring_cta?: boolean | null;
    recurring_cta_text?: string | null;
  }
): Promise<{ success: boolean; error?: any }> {
  try {
    // Build the update payload with only provided fields
    const payload: Record<string, any> = {
      user_id: userId,
      updated_at: new Date().toISOString()
    };

    if (changes.tone_of_voice !== undefined) {
      payload.tone_of_voice = changes.tone_of_voice;
    }
    if (changes.has_emojis !== undefined) {
      payload.has_emojis = changes.has_emojis;
    }
    if (changes.has_recurring_cta !== undefined) {
      payload.has_recurring_cta = changes.has_recurring_cta;
    }
    if (changes.recurring_cta_text !== undefined) {
      payload.recurring_cta_text = changes.recurring_cta_text;
    }

    const { error } = await supabase
      .from('user_planning_preferences')
      .upsert(payload as any, { 
        onConflict: 'user_id' 
      });

    if (error) {
      console.error('Error updating description preferences:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in updateDescriptionPreferencesPartial:', error);
    return { success: false, error };
  }
}

/**
 * Update disabled networks for a user
 */
export async function updateDisabledNetworks(
  userId: string,
  disabledNetworks: string[]
): Promise<{ success: boolean; error?: any }> {
  try {
    const { error } = await supabase
      .from('user_planning_preferences')
      .upsert({
        user_id: userId,
        disabled_networks: disabledNetworks,
        updated_at: new Date().toISOString()
      }, { 
        onConflict: 'user_id' 
      });

    if (error) {
      console.error('Error updating disabled networks:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in updateDisabledNetworks:', error);
    return { success: false, error };
  }
}

export interface ReorganizePlanningParams {
  user_id: string;
  include_statuses: string[];
  start_date?: string;
}

export async function invokeReorganizePlanning(
  params: ReorganizePlanningParams
): Promise<{ success: boolean; data?: { success: boolean; updatedCount?: number; error?: string }; error?: unknown }> {
  try {
    const body: Record<string, unknown> = {
      user_id: params.user_id,
      include_statuses: params.include_statuses,
    };
    if (params.start_date) {
      body.start_date = params.start_date;
    }
    const { data, error } = await supabase.functions.invoke('reorganize-planning', { body });
    if (error) return { success: false, error };
    return { success: true, data: data as { success: boolean; updatedCount?: number; error?: string } };
  } catch (err) {
    console.error('Error invoking reorganize-planning:', err);
    return { success: false, error: err };
  }
}
